import { PLAYER_AVATARS, avatarFor } from '../core/player-identity.js';
import { queryOne } from '../core/dom.js';

function loadScriptOnce(src) {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing?.dataset.loaded === 'true') return Promise.resolve();
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    }, { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.appendChild(script);
  });
}

export class PlayerProfileController {
  constructor({
    identity,
    baseUrl,
    fetchImpl = (...args) => fetch(...args),
    canChangeIdentity = () => true,
    onProfileChanged = () => {}
  }) {
    this.identity = identity;
    this.baseUrl = String(baseUrl || '').replace(/\/$/, '');
    this.fetchImpl = fetchImpl;
    this.canChangeIdentity = canChangeIdentity;
    this.onProfileChanged = onProfileChanged;
    this.selectedAvatarId = null;
    this.googleReady = false;
  }

  async mount() {
    queryOne('#playerProfileBtn')?.addEventListener('click', () => this.open());
    queryOne('#playerProfileCloseBtn')?.addEventListener('click', () => queryOne('#playerProfileDialog')?.close());
    queryOne('#playerProfileForm')?.addEventListener('submit', event => void this.saveProfile(event));
    queryOne('#playerSignOutBtn')?.addEventListener('click', () => void this.signOut());

    this.renderAvatarPicker();
    try {
      await this.identity.ensureGuest();
      this.render();
      this.onProfileChanged(this.identity.profile);
    } catch {
      this.renderUnavailable();
    }
  }

  renderAvatarPicker() {
    const root = queryOne('#playerAvatarPicker');
    if (!root) return;
    root.innerHTML = '';
    for (const avatar of PLAYER_AVATARS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'player-avatar-option';
      button.dataset.avatarId = avatar.id;
      button.setAttribute('aria-label', avatar.label);
      button.innerHTML = `<span aria-hidden="true">${avatar.symbol}</span><small>${avatar.label}</small>`;
      button.addEventListener('click', () => {
        this.selectedAvatarId = avatar.id;
        this.syncAvatarSelection();
      });
      root.appendChild(button);
    }
  }

  syncAvatarSelection() {
    document.querySelectorAll('[data-avatar-id]').forEach(button => {
      const active = button.dataset.avatarId === this.selectedAvatarId;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  renderUnavailable() {
    const label = queryOne('#playerProfileLabel');
    const avatar = queryOne('#playerProfileAvatar');
    if (label) label.textContent = 'Khách';
    if (avatar) avatar.textContent = '🦊';
  }

  render() {
    const profile = this.identity.profile;
    if (!profile) return this.renderUnavailable();

    const avatar = avatarFor(profile.avatarId);
    const topAvatar = queryOne('#playerProfileAvatar');
    const topLabel = queryOne('#playerProfileLabel');
    if (topAvatar) topAvatar.textContent = avatar.symbol;
    if (topLabel) topLabel.textContent = profile.displayName || 'Khách';

    const accountBadge = queryOne('#playerAccountBadge');
    const accountDescription = queryOne('#playerAccountDescription');
    const signOut = queryOne('#playerSignOutBtn');
    if (accountBadge) accountBadge.textContent = profile.accountType === 'google' ? 'Tài khoản IVS' : 'Guest';
    if (accountDescription) {
      accountDescription.textContent = profile.accountType === 'google'
        ? (profile.email || 'Đã liên kết tài khoản Google')
        : 'Bạn đang chơi ngay bằng Guest. Có thể lưu tài khoản để dùng lại trên thiết bị khác.';
    }
    signOut?.classList.toggle('hidden', profile.accountType !== 'google');

    const nameInput = queryOne('#playerDisplayName');
    if (nameInput) nameInput.value = profile.displayName || '';
    this.selectedAvatarId = profile.avatarId || 'fox';
    this.syncAvatarSelection();
  }

  async open() {
    const dialog = queryOne('#playerProfileDialog');
    if (!dialog) return;
    this.render();
    queryOne('#playerProfileStatus').textContent = '';
    dialog.showModal();
    await this.prepareGoogleSignIn();
  }

  async saveProfile(event) {
    event.preventDefault();
    const button = queryOne('#savePlayerProfileBtn');
    const status = queryOne('#playerProfileStatus');
    if (button) {
      button.disabled = true;
      button.textContent = 'Đang lưu...';
    }
    if (status) status.textContent = '';

    try {
      await this.identity.updateProfile({
        displayName: queryOne('#playerDisplayName')?.value || '',
        avatarId: this.selectedAvatarId || 'fox'
      });
      this.render();
      this.onProfileChanged(this.identity.profile);
      if (status) status.textContent = 'Đã lưu hồ sơ người chơi.';
    } catch (error) {
      if (status) status.textContent = error.message || 'Không thể lưu hồ sơ.';
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = 'Lưu hồ sơ';
      }
    }
  }

  async prepareGoogleSignIn() {
    const mount = queryOne('#googleSignInMount');
    const status = queryOne('#googleSignInStatus');
    if (!mount || !status) return;

    if (this.identity.isRegistered) {
      mount.innerHTML = '';
      status.textContent = 'Tài khoản này đã được lưu và có thể dùng trên thiết bị khác.';
      return;
    }

    status.textContent = 'Đang kiểm tra đăng nhập Google...';
    try {
      const response = await this.fetchImpl(`${this.baseUrl}/api/identity/config`, {
        headers: { Accept: 'application/json' }
      });
      const config = await response.json();
      if (!response.ok || !config.googleSignInEnabled || !config.googleClientId) {
        mount.innerHTML = '';
        status.textContent = 'Google Sign-In chưa được cấu hình trên hệ thống.';
        return;
      }

      await loadScriptOnce('https://accounts.google.com/gsi/client');
      if (!globalThis.google?.accounts?.id) throw new Error('Google Identity Services không khả dụng');

      mount.innerHTML = '';
      globalThis.google.accounts.id.initialize({
        client_id: config.googleClientId,
        callback: response => void this.handleGoogleCredential(response?.credential)
      });
      globalThis.google.accounts.id.renderButton(mount, {
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text: 'continue_with',
        locale: 'vi',
        width: 280
      });
      status.textContent = 'Đăng nhập để giữ nguyên nickname, avatar và quyền sở hữu trận.';
      this.googleReady = true;
    } catch (error) {
      mount.innerHTML = '';
      status.textContent = error.message || 'Không thể tải Google Sign-In.';
    }
  }

  async handleGoogleCredential(credential) {
    const status = queryOne('#googleSignInStatus');
    if (!credential) {
      if (status) status.textContent = 'Không nhận được thông tin xác thực Google.';
      return;
    }
    if (!this.canChangeIdentity()) {
      if (status) status.textContent = 'Hãy rời phòng LIVE hiện tại trước khi đổi tài khoản.';
      return;
    }

    try {
      await this.identity.signInWithGoogleCredential(credential);
      this.render();
      this.onProfileChanged(this.identity.profile);
      if (status) status.textContent = 'Đã lưu tài khoản. Player ID hiện tại được giữ nguyên.';
      await this.prepareGoogleSignIn();
    } catch (error) {
      if (status) status.textContent = error.message || 'Không thể đăng nhập Google.';
    }
  }

  async signOut() {
    const status = queryOne('#playerProfileStatus');
    if (!this.canChangeIdentity()) {
      if (status) status.textContent = 'Hãy rời phòng LIVE hiện tại trước khi đăng xuất.';
      return;
    }

    try {
      globalThis.google?.accounts?.id?.disableAutoSelect?.();
      await this.identity.logout();
      await this.identity.ensureGuest();
      this.render();
      this.onProfileChanged(this.identity.profile);
      if (status) status.textContent = 'Đã chuyển sang Guest mới trên thiết bị này.';
      await this.prepareGoogleSignIn();
    } catch (error) {
      if (status) status.textContent = error.message || 'Không thể tạo phiên Guest mới.';
    }
  }
}
